import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Info } from 'lucide-react';

const ROLES = ['team-member', 'current-user'];

// Ordered so composed copy always reads LinkedIn → Gmail → Google Calendar.
// `connect` is the noun in "connect your …"; `signal` is the noun in
// "missing … signals".
const SOURCE_LABELS = {
  linkedin: { connect: 'LinkedIn', signal: 'LinkedIn' },
  gmail: { connect: 'Gmail', signal: 'email' },
  calendar: { connect: 'Google Calendar', signal: 'calendar' },
};

const joinWithConjunction = (items, conjunction) => {
  if (items.length <= 1) return items[0] || '';
  if (items.length === 2) return `${items[0]} ${conjunction} ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, ${conjunction} ${items[items.length - 1]}`;
};

// Figma 61667:26617 — the copy-message a team admin sends a teammate. Absolute
// URL because the recipient opens it from outside the app.
const buildCopyMessage = ({ firstName, integrationLabel, networkSlug }) => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const lines = [
    `Hey ${firstName || '[FirstName]'}, we're getting the whole team connected on Getro. ` +
      `When you have a minute, could you connect your ${integrationLabel}?`,
    '',
    'Each connection means more accurate relationship ratings and warmer intros across our network — ' +
      'your data sharpens it for everyone.',
  ];
  // Only include the setup link when we have a real slug — otherwise the
  // recipient would get a broken `/networks/[network-slug]/...` URL.
  if (networkSlug) {
    lines.push('', `Setup: ${origin}/networks/${networkSlug}/settings/integrations`);
  }
  lines.push('', 'Thanks!');
  return lines.join('\n');
};

const Panel = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-top: 12px;
  width: 100%;
  font-style: italic;
  color: ${({ theme }) => theme.colors.text.subtle};
`;

const IconWrap = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  margin-top: 2px;
`;

const Text = styled.p`
  margin: 0;
  flex: 1 0 0;
  font-size: 14px;
`;

const Action = styled.button`
  font: inherit;
  color: inherit;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
  &:disabled {
    cursor: default;
    text-decoration: none;
  }
`;

const ActionLink = styled.a`
  font: inherit;
  color: inherit;
  text-decoration: underline;
`;

const CopiedHint = styled.span`
  margin-left: 4px;
  font-style: normal;
  color: ${({ theme }) => theme.colors.green[600]};
`;

const RelationshipStrengthInfoMessage = ({
  audience,
  missingLinkedIn,
  missingEmail,
  missingCalendar,
  firstName,
  networkSlug,
  onAction,
  onConnectClick,
  className,
}) => {
  const [justCopied, setJustCopied] = useState(false);
  const copiedTimerRef = useRef(null);
  useEffect(
    () => () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    },
    [],
  );

  const missingSources = [missingLinkedIn && 'linkedin', missingEmail && 'gmail', missingCalendar && 'calendar'].filter(
    Boolean,
  );
  if (missingSources.length === 0) return null;

  const isCurrentUser = audience === 'current-user';
  const subject = isCurrentUser ? "You haven't" : `${firstName || '[First name]'} hasn't`;
  const connectList = joinWithConjunction(
    missingSources.map((source) => SOURCE_LABELS[source].connect),
    'or',
  );

  // "Public signals only" is the no-private-signal threshold — LinkedIn AND
  // Gmail both missing. A missing calendar alone is a partial gap, so the copy
  // names the specific missing signals instead.
  const noPrivateSignals = missingLinkedIn && missingEmail;
  const reason = noPrivateSignals
    ? "we're using public signals only"
    : `we're missing ${joinWithConjunction(
        missingSources.map((source) => SOURCE_LABELS[source].signal),
        'and',
      )} signals`;
  const base = `${subject} connected ${connectList} yet, so ${reason}`;
  const text = isCurrentUser ? `${base}.` : `${base}. Help them get set up —`;

  const dataMissing = missingSources.join('-');
  // `data-action` stays a stable lowercase key (analytics hook); the visible
  // label is capitalized per the copy.
  const actionKey = isCurrentUser ? 'connect now' : 'copy message';
  const actionLabel = isCurrentUser ? 'Connect now' : 'copy message';

  const handleCopy = async () => {
    const integrationLabel = joinWithConjunction(
      missingSources.map((source) => SOURCE_LABELS[source].connect),
      'and',
    );
    const body = buildCopyMessage({ firstName, integrationLabel, networkSlug });
    try {
      await navigator.clipboard.writeText(body);
      setJustCopied(true);
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setJustCopied(false), 2000);
      if (onConnectClick) onConnectClick('copy');
    } catch {
      /* clipboard unavailable — leave the button in its idle state */
    }
    if (onAction) onAction(body);
  };

  let actionNode;
  if (isCurrentUser && networkSlug) {
    actionNode = (
      <ActionLink
        href={`/networks/${networkSlug}/settings/integrations`}
        data-action={actionKey}
        onClick={() => onConnectClick && onConnectClick('connect')}
      >
        {actionLabel}
      </ActionLink>
    );
  } else if (!isCurrentUser) {
    actionNode = (
      <>
        <Action type="button" onClick={handleCopy} data-action={actionKey}>
          {actionLabel}
        </Action>
        {justCopied && <CopiedHint aria-live="polite">Copied!</CopiedHint>}
      </>
    );
  } else {
    actionNode = (
      <Action type="button" onClick={onAction} disabled={!onAction} data-action={actionKey}>
        {actionLabel}
      </Action>
    );
  }

  return (
    <Panel className={className} data-audience={audience} data-missing={dataMissing}>
      <IconWrap aria-hidden="true">
        <Info size={16} />
      </IconWrap>
      <Text>
        {text} {actionNode}.
      </Text>
    </Panel>
  );
};

RelationshipStrengthInfoMessage.propTypes = {
  audience: PropTypes.oneOf(ROLES).isRequired,
  missingLinkedIn: PropTypes.bool.isRequired,
  missingEmail: PropTypes.bool.isRequired,
  missingCalendar: PropTypes.bool,
  // Used for the {name} interpolation when audience === 'team-member'.
  firstName: PropTypes.string,
  // Lets the "connect now" link / copy-message resolve to the network's integrations page.
  networkSlug: PropTypes.string,
  onAction: PropTypes.func,
  onConnectClick: PropTypes.func,
  className: PropTypes.string,
};

RelationshipStrengthInfoMessage.defaultProps = {
  missingCalendar: false,
  firstName: undefined,
  networkSlug: undefined,
  onAction: undefined,
  onConnectClick: undefined,
  className: undefined,
};

export default RelationshipStrengthInfoMessage;
